/**
 * What an Order position means, kept out of the components that show it.
 *
 * A field may declare where its column sits in the published header (ADR-0058).
 * Before that it sat wherever the pipeline's tie-breaks put it: the statistic
 * block was sorted by *emitted key* and then rendered under Stat Dictionary
 * display names, so `Total Tackles` landed at *d* for `def_tackles` and `Yards -
 * Passing` at *p* for `passing.yards` — sorted, and indistinguishable from
 * random to the person opening the file.
 *
 * The server's rule is `app/domain/column_order.py` and this module mirrors it.
 * Three parts of that rule are load-bearing here:
 *
 * - **Nothing declared sorts last.** Null is not position nought. Numbering the
 *   three statistics that matter must not promote the other sixty above them,
 *   which is what lets a reviewer adopt this one field at a time, and what
 *   keeps a profile nobody has touched publishing the header it always did.
 * - **The two blocks never interleave.** Statistics and carried fields are
 *   separate runs of columns in the raw file and §11 keeps them apart: the
 *   statistics are the modelled columns, the carried fields are what came along
 *   untouched. Both number from 1, so a carried field at 1 is first *among the
 *   carried*, not first in the file.
 * - **One Binding, one position.** A row on these screens is a Binding, and a
 *   Binding is what carries a position — so a position is assigned per row and
 *   never per column. That is what keeps a field whose columns fan out across
 *   reports (ADR-0055) at a single place in the header: the fan-out happens on
 *   the server, from one row that was never split here.
 *
 * **Positions are rewritten rather than inferred from array order**, which is
 * the whole reason `renumber` and `moveRow` exist. Both reads of a profile's
 * Bindings come back ordered by the server, so an order held only as the
 * position of a row in an array does not survive a save.
 *
 * **Where the mirror is approximate, and why that is safe.** The server ties
 * undeclared statistics by the *emitted* key, which on a multi-Feed profile is
 * qualified by a Feed namespace this screen does not compose. So the tie-break
 * here is the statistic's key where a row has one and its path otherwise:
 * exact for the single-Feed profiles that are most of this portal, and near
 * enough elsewhere that the sequence a reviewer sees is the sequence they get.
 * It is only ever a *tie*-break — every position somebody actually declares is
 * honoured exactly — and `renumber` turns the approximation into a declaration,
 * after which the server is reading numbers rather than breaking ties.
 *
 * No imports, so `npm test` can execute it directly: the frontend ships no test
 * runner and bare `node --experimental-strip-types` cannot load React or Vite.
 */

/**
 * The largest position the API will store (`BindingIn.sort_order`, `le=10000`).
 *
 * Mirrored so the cell refuses what the save would refuse, rather than offering
 * a number and then failing on it. Unlike the Weight ceiling there is no band
 * the API keeps legal but the screen should warn about: a position is either
 * storable or it is a typo.
 */
export const ORDER_CEILING = 10000;

/** Field Roles that publish a column a position can move. */
const ORDERABLE_ROLES = ["stat", "carried"] as const;

/** Which block a field's column belongs to, or null for one that publishes none. */
export type OrderBlock = (typeof ORDERABLE_ROLES)[number];

/**
 * A row as this module needs it.
 *
 * `key` identifies the Binding this row edits — a path per Feed, because
 * `yards` in the passing report and `yards` in the rushing one are two Bindings
 * meaning two different things. It is also what a position is assigned per, so
 * it has to be the row's real identity rather than a label.
 */
export interface OrderedField {
  key: string;
  path: string;
  role: string;
  /** The statistic this row binds, where it binds one. The server's own
   *  tie-break is the emitted key, and for a single-Feed profile that is this. */
  statKey?: string | null;
  /** The declared position, or null for nothing declared — which sorts last. */
  sortOrder: number | null;
  /** Which Feed published this column, by position in the Feed list. */
  feedPosition: number;
}

/** A refusal with a message, or a position — `null` being nothing declared. */
export type ParsedOrder =
  | { ok: true; sortOrder: number | null }
  | { ok: false; message: string };

/**
 * What was typed into an Order box.
 *
 * Blank is handled first and on purpose: `Number("")` and `Number(" ")` are
 * both `0`, so parsing before testing for blank would read a stray space as a
 * declared position — and then refuse it as nought, against a row the reviewer
 * had only cleared.
 */
export function parseOrder(text: string): ParsedOrder {
  const trimmed = text.trim();
  if (trimmed === "") return { ok: true, sortOrder: null };

  // `Number` rather than `parseInt`, which reads "3rd" as 3 and "1.2.3" as 1 —
  // taking the leading digits of something nobody meant as a number and
  // publishing a header by them.
  const value = Number(trimmed);
  if (!Number.isFinite(value)) {
    return { ok: false, message: "That is not a number. A position is a whole number, like 1." };
  }
  if (!Number.isInteger(value)) {
    // Refused rather than rounded: rounding accepts 1.5, stores 2, and shows
    // the reviewer the 1.5 they typed against a column that moved somewhere
    // else.
    return { ok: false, message: "A position is a whole number. 1 is the first column." };
  }
  if (value < 1) {
    return {
      ok: false,
      message:
        "A position starts at 1, which is the first column. Clear the box to leave this " +
        "field where the header already puts it.",
    };
  }
  if (value > ORDER_CEILING) {
    return { ok: false, message: `Above ${ORDER_CEILING}, which is more than a position can be.` };
  }
  return { ok: true, sortOrder: value };
}

/**
 * Which block a Field Role publishes into, or null where it publishes none.
 *
 * Takes the role rather than a row, because the Order cell knows a role and
 * would otherwise have to compose a whole row to ask — a synthetic row with a
 * made-up key, which is the kind of thing that later gets read as real.
 */
export function blockForRole(role: string): OrderBlock | null {
  return (ORDERABLE_ROLES as readonly string[]).includes(role) ? (role as OrderBlock) : null;
}

/** Which block this row's column lands in, or null where it publishes none. */
export function blockOf(row: OrderedField): OrderBlock | null {
  return blockForRole(row.role);
}

/**
 * Where a row sorts when nothing is declared: after everything that is.
 *
 * `Infinity` rather than a large number, so there is no ceiling a real position
 * could reach and overtake it. The server uses `math.inf` for the same reason.
 */
const UNDECLARED = Number.POSITIVE_INFINITY;

/** The block order in the file: statistics, then the carried fields. */
const BLOCK_RANK: Record<OrderBlock, number> = { stat: 0, carried: 1 };

/** What the server breaks a tie by, as near as this screen can compose it. */
function tieBreak(row: OrderedField): string {
  return blockOf(row) === "stat" ? (row.statKey ?? row.path) : row.path;
}

/**
 * Sort rows into the sequence the published file will use.
 *
 * The comparison is block, then declared position, then the tie-break — which
 * is why a row with nothing declared does not move: every comparison against
 * it falls through to the same answer the header had before any position
 * existed.
 *
 * A row publishing no column (the name, the team, an ignored field) sorts after
 * both blocks. Those have their own fixed place in the header and a position
 * typed against them changes nothing, so floating them to the top would have
 * the table imply otherwise.
 */
export function compareForPublication(a: OrderedField, b: OrderedField): number {
  const blockA = blockOf(a);
  const blockB = blockOf(b);
  const rankA = blockA === null ? ORDERABLE_ROLES.length : BLOCK_RANK[blockA];
  const rankB = blockB === null ? ORDERABLE_ROLES.length : BLOCK_RANK[blockB];
  if (rankA !== rankB) return rankA - rankB;

  const positionA = a.sortOrder ?? UNDECLARED;
  const positionB = b.sortOrder ?? UNDECLARED;
  if (positionA !== positionB) return positionA - positionB;

  // The carried block was feed-outer, path-inner on the server, so the Feed
  // leads for it. For the statistic block both ends agree whenever a row has a
  // statistic, which is every row the block contains.
  if (a.feedPosition !== b.feedPosition) return a.feedPosition - b.feedPosition;
  return tieBreak(a).localeCompare(tieBreak(b));
}

/** Each block's rows, in published order, keyed by block. */
function byBlock(rows: readonly OrderedField[]): Map<OrderBlock, OrderedField[]> {
  const blocks = new Map<OrderBlock, OrderedField[]>();
  for (const row of [...rows].sort(compareForPublication)) {
    const block = blockOf(row);
    if (block === null) continue;
    const members = blocks.get(block);
    if (members) members.push(row);
    else blocks.set(block, [row]);
  }
  return blocks;
}

/**
 * Every row, with each block's positions rewritten as 1..n over its current order.
 *
 * **One position per row, because one row is one Binding.** A field given a
 * statistic per report is several rows, each publishing its own column, and
 * each therefore gets its own number — collapsing them to one would overwrite
 * a per-report placement somebody had made deliberately. A field whose columns
 * fan out is the other case and needs nothing done: it is a single row here,
 * and the server spreads its one position across the reports.
 *
 * A row publishing no column is left undeclared rather than numbered, because a
 * position against it would be a number that does nothing.
 */
export function renumber(rows: readonly OrderedField[]): OrderedField[] {
  const assigned = new Map<string, number>();
  for (const [, members] of byBlock(rows)) {
    members.forEach((row, index) => assigned.set(row.key, index + 1));
  }
  return rows.map((row) => {
    const position = blockOf(row) === null ? null : (assigned.get(row.key) ?? null);
    return position === row.sortOrder ? row : { ...row, sortOrder: position };
  });
}

/**
 * Move one row one place up or down inside its own block.
 *
 * The block is renumbered first: a move inside a block nobody has numbered has
 * to declare positions for everything it passes, or the row moves on the screen
 * and the file does not change at all.
 *
 * Returns the rows unchanged — the same array — when the row is already at the
 * end it is being moved towards, so a caller can treat identity as "nothing
 * happened" and leave the save button alone.
 */
export function moveRow(
  rows: readonly OrderedField[],
  key: string,
  direction: "up" | "down",
): OrderedField[] {
  const target = rows.find((row) => row.key === key);
  const block = target ? blockOf(target) : null;
  if (!target || block === null) return rows as OrderedField[];

  const numbered = renumber(rows);
  const members = byBlock(numbered).get(block) ?? [];
  const at = members.findIndex((row) => row.key === key);
  const swapWith = direction === "up" ? members[at - 1] : members[at + 1];
  if (at < 0 || swapWith === undefined) return rows as OrderedField[];

  const from = members[at]!.sortOrder;
  const to = swapWith.sortOrder;
  return numbered.map((row) => {
    if (row.key === key) return { ...row, sortOrder: to };
    if (row.key === swapWith.key) return { ...row, sortOrder: from };
    return row;
  });
}

/** Which way a row can be nudged. Both true for a row with nowhere to go. */
export function nudgeLimits(
  rows: readonly OrderedField[],
): (key: string) => { up: boolean; down: boolean } {
  const limits = new Map<string, { up: boolean; down: boolean }>();
  for (const [, members] of byBlock(rows)) {
    members.forEach((row, index) =>
      limits.set(row.key, { up: index === 0, down: index === members.length - 1 }),
    );
  }
  return (key: string) => limits.get(key) ?? { up: true, down: true };
}

/**
 * What to say about the positions in this review, or null when there is nothing.
 *
 * Only one thing: two rows holding one position inside one block. The column
 * has no unique constraint on purpose — requiring one would make renumbering a
 * table a sequence of PUTs that collide halfway through — so a duplicate is
 * legal, and it is resolved by the tie-break. That resolution is nobody's
 * decision, which is exactly why it is worth showing rather than letting the
 * header come back in an order no one chose.
 *
 * It says nothing about a review that numbers some fields and not others. That
 * is the normal, intended state — the feature is adoptable one field at a time
 * — so a notice there would fire on ordinary work rather than on a mistake,
 * which is the mistake the Weight column's removed warnings already made.
 */
/**
 * How many colliding fields to name before counting the rest.
 *
 * **Because typing 1 into every box is a thing people do**, and it is not
 * even unreasonable — it is what "put them all at the front" looks like before
 * you know the positions are a sequence. Measured on a 47-statistic source,
 * naming them all produced a 507-character banner; the PFF profile has 248 and
 * would produce a wall. Three names and a count says the same thing, and says
 * the *number*, which is the part that tells somebody they have done this to
 * the whole review rather than to a pair of rows.
 */
const NAMES_SHOWN = 3;

function nameSome(paths: readonly string[]): string {
  if (paths.length <= NAMES_SHOWN + 1) return paths.join(", ");
  const rest = paths.length - NAMES_SHOWN;
  return `${paths.slice(0, NAMES_SHOWN).join(", ")} and ${rest} other fields`;
}

export function orderNotice(rows: readonly OrderedField[]): string | null {
  const slots = new Map<string, OrderedField[]>();
  for (const row of rows) {
    const block = blockOf(row);
    if (block === null || row.sortOrder === null) continue;
    // Block and position identify a slot. Padded so "10" does not sort before
    // "2" when the slots are walked in order to report the first collision.
    const slot = `${block}:${String(row.sortOrder).padStart(6, "0")}`;
    const held = slots.get(slot);
    if (held) held.push(row);
    else slots.set(slot, [row]);
  }

  for (const slot of [...slots.keys()].sort()) {
    const held = slots.get(slot)!;
    if (held.length < 2) continue;
    const position = held[0]!.sortOrder;
    const paths = [...new Set(held.map((row) => row.path))].sort();
    // Two reports of one field rather than two fields, which reads as a
    // repetition unless it is named as what it is.
    const named =
      paths.length === 1 ? `${held.length} reports of ${paths[0]}` : nameSome(paths);
    // "a fallback" rather than "alphabetically": the carried block breaks a tie
    // by Feed first and only then by path, so naming one of the two rules would
    // be wrong about half the collisions it reports.
    return (
      `${named} claim position ${position}, so which of them comes first is decided by ` +
      `a fallback rule rather than by you. Give them separate positions, or press Renumber.`
    );
  }
  return null;
}
